/**
 * SEO HEADING STRUCTURE - Productivity Tool
 * =========================================
 * 
 * TITLE: Productivity Tool | CSW | Online Workspace | Productivity Tool | Study Stream
 * META DESCRIPTION: Free productivity tools with Pomodoro timer, task management...
 * 
 * H1 (1 tag):
 *   - "Productivity Tool" (hero section)
 * 
 * H2 (3 tags):
 *   - "Boost Your Productivity" (features section)
 *   - "Why Choose CSW?" (benefits section)
 *   - "Ready to Get More Done?" (CTA section)
 * 
 * H3 (10 tags):
 *   - Feature card titles (6): "Pomodoro Timer", "Task Management", etc.
 *   - Benefit titles (4): Dynamic benefit items
 */

import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Timer, ListChecks, BarChart3, Focus, Sparkles, Calendar, ArrowRight, Target } from "lucide-react";

const features = [
  {
    icon: Timer,
    title: "Pomodoro Timer",
    description: "Stay focused with customizable work intervals. Set your preferred focus and break times.",
  },
  {
    icon: ListChecks,
    title: "Task Management",
    description: "Organize your work with intuitive to-do lists and task boards.",
  },
  {
    icon: BarChart3,
    title: "Progress Analytics",
    description: "Visualize your productivity trends over time and optimize your schedule.",
  },
  {
    icon: Focus,
    title: "Focus Mode",
    description: "Block distractions and enter deep work without interruptions.",
  },
  {
    icon: Calendar,
    title: "Reminder Alerts",
    description: "Stay on track with timely notifications for tasks and deadlines.",
  },
  {
    icon: Sparkles,
    title: "Productivity Insights",
    description: "Get personalized recommendations based on your work habits.",
  },
];

export default function ProductivityTool() {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Productivity Tool | CSW | Online Workspace | Productivity Tool | Study Stream"
        description="Free productivity tools with Pomodoro timer, task management, progress analytics, and focus mode. Boost your study and work efficiency."
        keywords="productivity tool, pomodoro timer, focus tracker, task management, productivity app, study productivity"
        canonicalUrl="/productivity-tool/"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
          {/* Gradient Background */}
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-muted" />
          
          {/* Animated Glow Effects */}
          <div className="absolute top-20 left-20 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
          
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <div className="text-left space-y-8 animate-fade-in">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/50 border border-border backdrop-blur-sm">
                  <div className="w-2 h-2 bg-primary rounded-full animate-glow-pulse" />
                  <span className="text-sm font-medium text-muted-foreground">Work Smarter, Not Harder</span>
                </div>
                
                <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight">
                  <span className="text-gradient">Productivity Tool</span>
                </h1>
                
                <p className="text-xl sm:text-2xl text-muted-foreground font-light max-w-xl">
                  Everything you need to stay focused, organized, and productive.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Button 
                    size="lg" 
                    className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                    asChild
                  >
                    <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                      Get Started Free
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="border-2 border-border hover:bg-card font-semibold px-8 py-6 text-lg transition-all hover:scale-105"
                    asChild
                  >
                    <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                      See Demo
                    </a>
                  </Button>
                </div>
                
                <div className="flex items-center gap-8 pt-8">
                  <div>
                    <p className="text-3xl font-bold text-gradient">25min</p>
                    <p className="text-sm text-muted-foreground">Focus Sessions</p>
                  </div>
                  <div className="w-px h-12 bg-border" />
                  <div>
                    <p className="text-3xl font-bold text-gradient">Kanban</p>
                    <p className="text-sm text-muted-foreground">Task Boards</p>
                  </div>
                  <div className="w-px h-12 bg-border" />
                  <div>
                    <p className="text-3xl font-bold text-gradient">Free</p>
                    <p className="text-sm text-muted-foreground">Forever</p>
                  </div>
                </div>
              </div>
              
              {/* Right Video */}
              <div className="relative animate-fade-in" style={{ animationDelay: '0.2s' }}>
                <div className="relative rounded-3xl overflow-hidden border border-border/50 shadow-2xl">
                  <video 
                    src="/videos/productivity-demo.mp4" 
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                </div>
                
                {/* Floating Card */}
                <div className="absolute bottom-8 left-8 right-8 bg-card/90 backdrop-blur-md border border-border rounded-2xl p-6 shadow-xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Boost Your Output</p>
                      <p className="text-xl font-bold text-foreground">Stay Productive</p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                      <Target className="w-6 h-6 text-primary-foreground" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted to-background" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
          
          <div className="container mx-auto max-w-6xl relative z-10">
            <div className="text-center mb-16 animate-fade-in">
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                Tools That{" "}
                <span className="text-gradient">Work for You</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Powerful features designed to help you focus, organize, and accomplish more
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature) => {
                const Icon = feature.icon;
                return (
                  <Card
                    key={feature.title}
                    className="group bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 hover:shadow-xl"
                  >
                    <CardContent className="p-8 space-y-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      
                      <h3 className="text-xl font-semibold text-foreground font-display">
                        {feature.title}
                      </h3>
                      
                      <p className="text-muted-foreground leading-relaxed">
                        {feature.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-24 px-4 relative">
          <div className="absolute top-0 right-0 w-96 h-96 bg-secondary/10 rounded-full blur-[120px]" />
          
          <div className="container mx-auto max-w-4xl relative z-10">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                Why Our{" "}
                <span className="text-gradient">Productivity Tool</span>?
              </h2>
              <p className="text-xl text-muted-foreground">
                Designed for anyone who wants to accomplish more with less stress
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-8">
              {[
                { title: "Simple by design", desc: "No complicated setup or learning curve. Start being productive in seconds." },
                { title: "Proven techniques", desc: "Built on time-tested productivity methods like Pomodoro that actually work." },
                { title: "Flexible workflow", desc: "Adapts to your personal work style. Use only the features you need." },
                { title: "Always available", desc: "Access your productivity tools from any device, anywhere." },
              ].map((item, index) => (
                <div 
                  key={index}
                  className="p-6 rounded-2xl bg-card/50 backdrop-blur-sm border border-border/50 hover:border-primary/50 transition-all hover:shadow-lg"
                >
                  <h3 className="text-xl font-semibold text-foreground mb-3 font-display">{item.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-primary/20 rounded-full blur-[150px]" />
          
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 font-display">
              Start Being More{" "}
              <span className="text-gradient">Productive</span> Today
            </h2>
            <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
              Join thousands who have transformed their productivity with our simple yet powerful tools.
            </p>
            <Button 
              size="lg" 
              className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-10 py-7 text-lg glow-primary transition-all hover:scale-105"
              asChild
            >
              <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                Try It Free Now
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </Button>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}